import { useState, useRef, type FormEvent } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useUI } from '../../context/UIContext'
import { usePayment, NEDARIM_IFRAME_URL } from '../../hooks/usePayment'
import { useUtm } from '../../hooks/useUtm'
import { postLead } from '../../api/giving'
import Button from '../ui/Button'
import { Input, Textarea } from '../ui/Input'

const TIERS = window.campaignParams?.tiers || []

const DonationForm = () => {
  const { selectedTier, openModal, setIsVerifying, donationSuccess, closeModal } = useUI()
  const utm = useUtm()

  const iframeRef = useRef<HTMLIFrameElement>(null)
  const { initPayment, sendTransaction, requestHeight } = usePayment(iframeRef)

  const [fullName, setFullName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [donationMsg, setDonationMsg] = useState('')
  const [freeAmount, setFreeAmount] = useState('')
  const [useTashlumim, setUseTashlumim] = useState(false)
  const [freeTashlumim, setFreeTashlumim] = useState(2)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [showIframe, setShowIframe] = useState(false)
  const [iframeReady, setIframeReady] = useState(false)
  // null = no selection yet, 'free' = free-amount mode, otherwise a tier object
  const [localSelection, setLocalSelection] = useState<'free' | { monthly_tier: number; months: number; donation_amount: number } | null>(
    selectedTier !== null ? selectedTier : null
  )

  // Effective tier: use context (set when clicking tier buttons on main page) or local pick
  const effectiveTier = selectedTier ?? (localSelection !== 'free' ? localSelection : null)
  // A selection has been made when either a tier is chosen OR free mode is active
  const hasSelection = effectiveTier !== null || localSelection === 'free'

  // Derive payment values depending on whether a tier is selected
  const isTier = effectiveTier !== null
  const freeTotal = Number(freeAmount) || 0
  const effectiveTashlumim = useTashlumim ? freeTashlumim : 1
  const monthly_tier = isTier ? effectiveTier!.monthly_tier : Math.round(freeTotal / effectiveTashlumim)
  const months       = isTier ? effectiveTier!.months       : effectiveTashlumim
  const donation_amount = isTier ? effectiveTier!.donation_amount : freeTotal

  if (donationSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center gap-5 py-4 sm:py-10 text-center"
      >
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
          <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-[#2d2d2d]" style={{ fontFamily: 'FbShefa, Arial Hebrew, sans-serif' }}>
          תודה רבה!
        </h3>
        <p className="text-[#6b7280] text-base max-w-xs" style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}>
          תרומתך התקבלה בהצלחה. יישר כח על השתתפותך בחלום של לאה׳לע!
        </p>
        <Button variant="outline" onClick={closeModal}>סגירה</Button>
      </motion.div>
    )
  }

  const onIframeLoad = () => {
    requestHeight()
    setIframeReady(true)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setPaymentError(null)

    if (!isTier && (!freeAmount || Number(freeAmount) <= 0)) {
      setPaymentError('נא להזין סכום תרומה')
      return
    }

    if (!isAnonymous && !fullName.trim()) {
      setPaymentError('נא להזין שם מלא')
      return
    }

    setIsSubmitting(true)
    try {
      const { giving_id } = await postLead({
        full_name: fullName.trim(),
        display_name: displayName.trim(),
        is_anonymous: isAnonymous,
        email: email.trim(),
        phone: phone.trim(),
        monthly_tier,
        months,
        donation_amount,
        donation_msg: donationMsg.trim(),
        utm,
      })

      initPayment(
        {
          givingId: giving_id,
          firstName: fullName.trim(),
          phone: phone.trim(),
          mail: email.trim(),
          amount: monthly_tier,
          months,
          paymentType: (isTier || useTashlumim) ? 'HK' : 'Ragil',
        },
        (msg) => {
          setPaymentError(msg)
          setIsSending(false)
        },
        () => setIsSending(false)
      )

      setShowIframe(true)
      setIsVerifying(true)
    } catch {
      setPaymentError('אירעה שגיאה, נסו שוב')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col gap-0" dir="rtl">

      {/* ── Tier selection panel (shown until a selection is confirmed) ── */}
      {!showIframe && (
        <div className="mb-3">
          <h3 className="text-center text-[#E63946] text-xl font-bold mb-2" style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}>
            בחרו את ההנצחה שלכם
          </h3>
          <div className="flex flex-col lg:flex-row gap-2 lg:justify-center">
            {TIERS.map((tier) => {
              const da = tier.monthly_tier * tier.months
              const isSelected = effectiveTier?.monthly_tier === tier.monthly_tier && effectiveTier?.months === tier.months
              return (
                <button
                  key={tier.monthly_tier}
                  type="button"
                  onClick={() => {
                    openModal({ monthly_tier: tier.monthly_tier, months: tier.months, donation_amount: da })
                    setLocalSelection({ monthly_tier: tier.monthly_tier, months: tier.months, donation_amount: da })
                  }}
                  className={`flex flex-col items-center justify-between rounded px-5 py-3 border-1 transition-all cursor-pointer text-center ${
                    isSelected
                      ? 'bg-[#E63946] border-[#E63946] text-white'
                      : 'bg-white border-[#E63946] text-[#E63946] hover:bg-[#E63946]/10'
                  }`}
                  style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}
                >
                  <span className="font-bold text-base">{tier.label}</span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-1xl font-bold">{tier.monthly_tier.toLocaleString('he-IL')}</span>
                    <span className="text-sm font-medium">₪ × {tier.months}</span>
                  </div>
                </button>
              )
            })}
          </div>
          {/* Free-amount link */}
          <div className="mt-2 text-center">
            <button
              type="button"
              onClick={() => {
                openModal(null)
                setLocalSelection('free')
              }}
              className={`text-base font-bold underline cursor-pointer transition-colors ${
                localSelection === 'free' ? 'text-[#E63946]' : 'text-[#6b7280] hover:text-[#E63946]'
              }`}
              style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}
            >
              לתרומה בכל סכום
            </button>
          </div>
        </div>
      )}

      {/* ── Main form (only shown once a selection is made) ── */}
      <AnimatePresence>
        {hasSelection && (
          <motion.div
            key="form-content"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 items-start">

        {/* ── Right column: Amount box + Message (always visible, disabled after pay) ── */}
        <div className={`flex flex-col gap-6 md:col-start-1 transition-opacity duration-300 ${showIframe ? 'opacity-50 pointer-events-none' : ''}`}>

          {/* Amount Box */}
          <div className="border-2 border-[#45C1D8] rounded p-3">
            {isTier ? (
              /* Tier mode: show monthly × months = total */
              <div className="flex items-center justify-between gap-2">
                <span className="text-[#E63946] text-2xl font-bold" style={{ fontFamily: 'Almoni, Arial Hebrew, sans-serif' }}>
                  תרומתך:
                </span>
                <div className="flex items-baseline gap-1 flex-wrap justify-end">
                  <span className="text-3xl font-light text-transparent" style={{ WebkitTextStroke: '1px #E63946', fontFamily: 'Almoni, Arial Hebrew, sans-serif' }}>
                    {monthly_tier.toLocaleString('he-IL')}
                  </span>
                  <span className="text-xl font-light text-transparent" style={{ WebkitTextStroke: '1px #E63946', fontFamily: 'Almoni, Arial Hebrew, sans-serif' }}>
                    ₪ × {months}
                  </span>
                </div>
              </div>
            ) : (
              /* Free-amount mode: open input + tashlumim */
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    placeholder="הזן סכום"
                    value={freeAmount}
                    onChange={(e) => setFreeAmount(e.target.value)}
                    className="flex-1 text-3xl font-light text-[#E63946] border-b-2 border-[#E63946] bg-transparent outline-none text-right placeholder-[#E63946]/40"
                    style={{ fontFamily: 'Almoni, Arial Hebrew, sans-serif' }}
                  />
                  <span className="text-3xl font-light text-[#E63946]" style={{ fontFamily: 'Almoni, Arial Hebrew, sans-serif' }}>₪</span>
                </div>
                {/* Tashlumim checkbox + selector */}
                <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={useTashlumim}
                    onChange={(e) => setUseTashlumim(e.target.checked)}
                    className="w-4 h-4 accent-[#E63946] rounded-sm"
                  />
                  <span className="text-sm font-bold text-[#2d2d2d]" style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}>
                    חלוקה לתשלומים
                  </span>
                </label>
                {useTashlumim && (
                  <div className="flex flex-row items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[#2d2d2d]" style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}>מספר תשלומים:</span>
                      <select
                        value={freeTashlumim}
                        onChange={(e) => setFreeTashlumim(Number(e.target.value))}
                        className="h-9 px-2 rounded border border-[#E63946] text-[#E63946] text-sm font-bold bg-white cursor-pointer outline-none"
                        style={{ fontFamily: 'Almoni, Arial Hebrew, sans-serif' }}
                      >
                        {Array.from({ length: 35 }, (_, i) => i + 2).map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                    {freeAmount && Number(freeAmount) > 0 && (
                      <p className="text-xs text-[#6b7280]" style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}>
                        {freeTashlumim} תשלומים של {Math.round(Number(freeAmount) / freeTashlumim).toLocaleString('he-IL')} ₪
                      </p>
                    )}
                  </div>
                )}
                </div>
              </div>
            )}
          </div>

          {/* Donation Msg Box */}
          <div className="relative border-[3px] border-[#E63946] p-5 pt-10 mt-6 flex flex-col text-center">
            <div className="absolute -top-8 right-4 md:-top-10 md:-right-6 w-[80px] h-[80px] md:w-[100px] md:h-[100px] bg-[#E63946] text-white rounded-full flex items-center justify-center text-center shadow-lg transform -rotate-[15deg]">
              <span className="text-[14px] md:text-[17px] font-bold leading-[1.1]" style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}>
                הרגע<br />שלי עם<br />לאה׳לע
              </span>
            </div>
            <h4 className="text-[#E63946] text-[18px] md:text-[22px] font-bold mb-3 w-full" style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}>
              כאן תוכלו להביע את<br />הערכתכם ללאה׳לע המקסימה
            </h4>
            <Textarea
              className="w-full text-center text-[#9ca3af] min-h-[80px] md:min-h-[140px] text-base focus:text-[#2d2d2d] bg-transparent resize-none"
              style={{
                fontFamily: 'Almoni, Arial Hebrew, sans-serif',
                lineHeight: '2rem',
                backgroundImage: 'repeating-linear-gradient(to bottom, transparent, transparent calc(2rem - 1px), #fca5a5 calc(2rem - 1px), #fca5a5 2rem)',
                backgroundAttachment: 'local',
                paddingTop: '0.25rem',
              }}
              placeholder=""
              value={donationMsg}
              onChange={(e) => setDonationMsg(e.target.value)}
            />
          </div>
        </div>

        {/* ── Left column: Fields OR iframe ── */}
        <div className="md:col-start-2 md:row-span-2 flex flex-col gap-6 pt-2">
          <AnimatePresence mode="wait">
            {showIframe ? (
              <motion.div
                key="iframe"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex flex-col gap-4"
              >
                <h3 className="text-2xl font-bold text-center text-[#2d2d2d] mb-2" style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}>
                  הזנת פרטי אשראי
                </h3>
                {!iframeReady && (
                  <div className="flex justify-center items-center py-10">
                    <div className="w-10 h-10 border-4 border-[#1a6fad] border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                <iframe
                  ref={iframeRef}
                  src={NEDARIM_IFRAME_URL}
                  onLoad={onIframeLoad}
                  className="w-full border-0 transition-all duration-300"
                  style={{ minHeight: iframeReady ? 200 : 0, display: iframeReady ? 'block' : 'none' }}
                  title="Nedarim Plus Payment"
                />
                {iframeReady && (
                  <div className="flex flex-col items-center gap-2">
                    {paymentError && (
                      <p className="text-red-500 text-sm text-right w-full" style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}>
                        {paymentError}
                      </p>
                    )}
                    <Button
                      fullWidth
                      variant="secondary"
                      disabled={isSending}
                      onClick={() => {
                        setIsSending(true)
                        setPaymentError(null)
                        sendTransaction()
                      }}
                    >
                      {isSending ? (
                        <span className="flex items-center gap-2">
                          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                          מעבד תשלום...
                        </span>
                      ) : (
                        'אישור תשלום ←'
                      )}
                    </Button>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                onSubmit={handleSubmit}
                className="flex flex-col gap-3"
              >
                <Input
                  label='שם מלא (לקבלה)'
                  placeholder='שם מלא (לקבלה)'
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required={true}
                  variant="underline"
                />

                {!isAnonymous && (
                  <Input
                    label="השם שיוצג"
                    placeholder="שם לתצוגה באתר (חובה)"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required={true}
                    disabled={isAnonymous}
                    variant="underline"
                  />
                )}

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isAnonymous}
                    onChange={(e) => setIsAnonymous(e.target.checked)}
                    className="w-4 h-4 accent-gray-500 rounded-sm"
                  />
                  <span className="text-sm font-bold text-[#2d2d2d]" style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}>
                    תרום באופן אנונימי
                  </span>
                </label>

                <Input
                  label='דוא"ל'
                  type="email"
                  placeholder='הקלד את הדוא"ל שלך (חובה)'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  variant="underline"
                />

                <Input
                  label="טלפון"
                  type="tel"
                  placeholder=""
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  variant="underline"
                />

                {paymentError && (
                  <p className="text-red-500 text-sm text-right" style={{ fontFamily: 'FbAsli, Arial Hebrew, sans-serif' }}>
                    {paymentError}
                  </p>
                )}

                <Button
                  type="submit"
                  fullWidth
                  disabled={isSubmitting || (!isTier && (!freeAmount || Number(freeAmount) <= 0))}
                  variant="secondary"
                  className="mt-2 text-xl py-4 font-bold"
                >
                  {isSubmitting ? 'שולח...' : 'המשך לתרומה ←'}
                </Button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>

      </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  )
}

export default DonationForm
